import { InputAdornment } from '@mui/material'
import React, { forwardRef, useState } from 'react'
import {
  Button,
  IconButton,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { formRegex, tLabel, tMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type SignUpFormProps = {
  onSubmitSignUp: (inputs: SignUpFormValues) => Promise<boolean | void>
}

export type SignUpFormValues = {
  displayName: string
  email: string
  password: string
}

const schema: UseFormProps['schema'] = o => ({
  displayName: o
    .string()
    .max(50)
    .custom((value, helpers) => {
      // 特殊文字チェック
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel('signUp.displayName'),
          }),
        })
      }
      return value
    })
    .label(tLabel('signUp.displayName')),
  email: o
    .string()
    .regex(formRegex.email)
    .custom((value, helpers) => {
      // 先頭に記号があるかチェック
      const match = value.match(/^([=+\-@])/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputStartsWithSymbol', {
            symbol: match[1],
            label: tLabel('signUp.email'),
          }),
        })
      }
      // 特殊文字チェック
      const specialCharsRegex = /[=;\t\n\r,]/
      const specialCharsMatch = value.match(specialCharsRegex)
      if (specialCharsMatch) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel('signUp.email'),
          }),
        })
      }
      return value
    })
    .label(tLabel('signUp.email')),
  password: o.string().min(8).max(30).label(tLabel('signUp.password')),
})

/**
 *  SignUp Form Organism.
 *
 *  This component is organism layer but is named suffix xxForm,
 *  because all of form components belong to Organism.
 */
export const SignUpForm: Organism<SignUpFormProps> = ({ onSubmitSignUp }) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormValues>({
    schema,
  })

  return (
    <Form onSubmit={handleSubmit(onSubmitSignUp)} sx={{ pt: 3 }}>
      <TextInputCell
        type="text"
        error={errors?.displayName}
        {...register('displayName')}
      />
      <TextInputCell
        type="email"
        error={errors?.email}
        {...register('email')}
      />
      <PasswordInputCell error={errors?.password} {...register('password')} />

      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('signUp')

    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const PasswordInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't' | 'type'>, ref) => {
    const { t } = useTranslation('signUp')
    const [showPassword, setShowPassword] = useState(false)

    return (
      <CellFrame>
        <TextInputField
          type={showPassword ? 'text' : 'password'}
          {...props}
          textFieldProps={{
            ...props.textFieldProps,
            InputProps: {
              ...props.textFieldProps?.InputProps,
              inputProps: { variant: 'body2', sx: { px: 4, py: 3 } },
              endAdornment: (
                <InputAdornment position="end">
                  {showPassword ? (
                    <IconButton.Display
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  ) : (
                    <IconButton.Hide
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('signUp')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}
